import React from 'react';
import { Shield, Lock, Eye, Database, FileText } from 'lucide-react';

export const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <Shield className="w-16 h-16 text-amber-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Política de Privacidade
          </h1>
          <p className="text-gray-600">
            Última atualização: Janeiro de 2026
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-lg p-8 space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <FileText className="w-6 h-6 mr-2 text-amber-600" />
              1. Introdução
            </h2>
            <p className="text-gray-700 leading-relaxed">
              A GO VIP ("nós", "nosso" ou "empresa") está comprometida em proteger a sua privacidade. 
              Esta Política de Privacidade explica como recolhemos, usamos, divulgamos e protegemos as suas 
              informações pessoais quando utiliza o nosso website <strong>goviportugal.com</strong> e os nossos 
              serviços de transfers e tours.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              Esta política está em conformidade com o Regulamento Geral de Proteção de Dados (RGPD) da União Europeia 
              e a legislação portuguesa aplicável.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Database className="w-6 h-6 mr-2 text-amber-600" />
              2. Dados que Recolhemos
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">2.1 Informações que Fornece:</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li><strong>Nome completo</strong></li>
                  <li><strong>Email</strong></li>
                  <li><strong>Número de telefone</strong></li>
                  <li><strong>Data e horário da reserva</strong></li>
                  <li><strong>Número de passageiros</strong></li>
                  <li><strong>Mensagens e pedidos especiais</strong></li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">2.2 Informações Recolhidas Automaticamente:</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Endereço IP</li>
                  <li>Tipo de navegador</li>
                  <li>Páginas visitadas</li>
                  <li>Tempo de permanência no site</li>
                  <li>Cookies (ver Política de Cookies)</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Eye className="w-6 h-6 mr-2 text-amber-600" />
              3. Como Usamos os Seus Dados
            </h2>
            <p className="text-gray-700 mb-3">Utilizamos as suas informações para:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Processar e confirmar as suas reservas</li>
              <li>Comunicar consigo sobre os serviços</li>
              <li>Enviar confirmações e lembretes</li>
              <li>Melhorar os nossos serviços</li>
              <li>Cumprir obrigações legais</li>
              <li>Prevenir fraudes e garantir segurança</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Lock className="w-6 h-6 mr-2 text-amber-600" />
              4. Base Legal para Processamento
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Processamos os seus dados com base em:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
              <li><strong>Execução de contrato:</strong> Para fornecer os serviços que solicitou</li>
              <li><strong>Consentimento:</strong> Quando nos dá permissão explícita</li>
              <li><strong>Interesse legítimo:</strong> Para melhorar os nossos serviços</li>
              <li><strong>Obrigação legal:</strong> Para cumprir requisitos legais</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              5. Partilha de Dados
            </h2>
            <p className="text-gray-700 leading-relaxed">
              <strong>NÃO vendemos, alugamos ou partilhamos</strong> os seus dados pessoais com terceiros 
              para fins de marketing.
            </p>
            <p className="text-gray-700 leading-relaxed mt-3">
              Podemos partilhar dados apenas com:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
              <li>Prestadores de serviços essenciais (hospedagem, processamento de pagamentos)</li>
              <li>Autoridades legais, quando exigido por lei</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              6. Os Seus Direitos (RGPD)
            </h2>
            <p className="text-gray-700 mb-3">Tem o direito de:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Acesso:</strong> Solicitar cópia dos seus dados</li>
              <li><strong>Retificação:</strong> Corrigir dados incorretos</li>
              <li><strong>Eliminação:</strong> Solicitar a remoção dos seus dados</li>
              <li><strong>Portabilidade:</strong> Receber os seus dados em formato estruturado</li>
              <li><strong>Oposição:</strong> Opor-se ao processamento dos seus dados</li>
              <li><strong>Retirar consentimento:</strong> A qualquer momento</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Para exercer estes direitos, contacte: <strong>Fantasticasfalto@gmail.com</strong>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              7. Retenção de Dados
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Mantemos os seus dados apenas pelo tempo necessário para:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
              <li>Fornecer os serviços solicitados</li>
              <li>Cumprir obrigações legais (mínimo 5 anos para fins fiscais)</li>
              <li>Resolver disputas</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              8. Segurança dos Dados
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Implementamos medidas de segurança técnicas e organizacionais para proteger os seus dados:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
              <li>Encriptação SSL/TLS (HTTPS)</li>
              <li>Acesso restrito aos dados</li>
              <li>Backups regulares</li>
              <li>Monitorização de segurança</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              9. Cookies
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Utilizamos cookies essenciais para o funcionamento do website. 
              Para mais informações, consulte a nossa <a href="#" className="text-amber-600 hover:underline">Política de Cookies</a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              10. Menores de Idade
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Os nossos serviços não se destinam a menores de 18 anos. Não recolhemos intencionalmente 
              dados de menores sem consentimento parental.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              11. Alterações a Esta Política
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Podemos atualizar esta política periodicamente. A data da última atualização será sempre indicada no topo desta página.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              12. Contacto
            </h2>
            <div className="bg-amber-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-2"><strong>GO VIP</strong></p>
              <p className="text-gray-700">Email: Fantasticasfalto@gmail.com</p>
              <p className="text-gray-700">Telefone: +351 932 651 940</p>
              <p className="text-gray-700">Website: goviportugal.com</p>
            </div>
          </section>

          <section className="border-t pt-6">
            <p className="text-sm text-gray-600 italic">
              Se tiver questões sobre esta Política de Privacidade ou sobre como tratamos os seus dados, 
              não hesite em contactar-nos. Tem também o direito de apresentar uma reclamação à 
              Comissão Nacional de Proteção de Dados (CNPD) em <a href="https://www.cnpd.pt" target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:underline">www.cnpd.pt</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};
